import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Search } from 'lucide-react';
import Checkbox from './Checkbox';

/**
 * DataTable Component
 * 
 * Enterprise-grade data table with sorting, filtering, pagination, and row selection.
 * Built with accessibility and mobile responsiveness in mind.
 * 
 * @example
 * ```tsx
 * <DataTable
 *   columns={[
 *     { accessor: 'name', header: 'Name', sortable: true },
 *     { accessor: 'amount', header: 'Amount', cell: (row) => `₹${row.amount}` }
 *   ]}
 *   data={expenses}
 *   onRowSelect={(rows) => console.log(rows)}
 * />
 * ```
 */

export interface Column<T = any> {
  /** Unique key for the column */
  accessor: keyof T | string;
  /** Column header text */
  header: string;
  /** Custom cell renderer */
  cell?: (row: T, rowIndex: number) => React.ReactNode;
  /** Enable sorting for this column */
  sortable?: boolean;
  /** Enable filtering for this column */
  filterable?: boolean;
  /** Column width (CSS value) */
  width?: string;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Hide on mobile */
  hideOnMobile?: boolean;
}

export interface DataTableProps<T = any> {
  /** Column configuration */
  columns: Column<T>[];
  /** Data array */
  data: T[];
  /** Enable row selection */
  selectable?: boolean;
  /** Selected row callback */
  onRowSelect?: (selectedRows: T[]) => void;
  /** Enable search */
  searchable?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Custom search function */
  onSearch?: (query: string) => void;
  /** Enable pagination */
  paginated?: boolean;
  /** Items per page */
  pageSize?: number;
  /** Loading state */
  loading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Additional class */
  className?: string;
}

type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
  key: string;
  direction: SortDirection;
}

const DataTable = <T extends Record<string, any>>({
  columns,
  data,
  selectable = false,
  onRowSelect,
  searchable = false,
  searchPlaceholder = 'Search...',
  onSearch,
  paginated = false,
  pageSize = 10,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
}: DataTableProps<T>) => {
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Handle sorting
  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return data;

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [data, sortConfig]);

  // Handle search
  const filteredData = useMemo(() => {
    if (!searchQuery) return sortedData;

    return sortedData.filter((row) => {
      return Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [sortedData, searchQuery]);

  // Handle pagination
  const paginatedData = useMemo(() => {
    if (!paginated) return filteredData;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize, paginated]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  // Toggle sort
  const handleSort = (columnKey: string) => {
    let direction: SortDirection = 'asc';

    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'asc') direction = 'desc';
      else if (sortConfig.direction === 'desc') direction = null;
    }

    setSortConfig({ key: columnKey, direction });
  };

  // Toggle row selection
  const handleRowSelect = (rowIndex: number) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
    
    const selectedData = Array.from(newSelected).map(index => filteredData[index]);
    onRowSelect?.(selectedData);
  };

  // Toggle all rows
  const handleSelectAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
      onRowSelect?.([]);
    } else {
      const allIndexes = paginatedData.map((_, index) => (currentPage - 1) * pageSize + index);
      setSelectedRows(new Set(allIndexes));
      onRowSelect?.(paginatedData);
    }
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page
    onSearch?.(query);
  };

  // Get sort icon
  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown size={16} className="text-gray-400" />;
    }
    if (sortConfig.direction === 'asc') {
      return <ChevronUp size={16} className="text-primary-600" />;
    }
    if (sortConfig.direction === 'desc') {
      return <ChevronDown size={16} className="text-primary-600" />;
    }
    return <ChevronsUpDown size={16} className="text-gray-400" />;
  };

  // Get cell alignment class
  const getAlignmentClass = (align?: 'left' | 'center' | 'right') => {
    switch (align) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Search Bar */}
      {searchable && (
        <div className="mb-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg bg-white dark:bg-dark-card dark:border-dark-border text-gray-900 dark:text-dark-text placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
              aria-label="Search table"
            />
          </div>
        </div>
      )}

      {/* Table Container */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card">
        <table 
          className="w-full"
          role="table"
          aria-label="Data table"
        >
          {/* Table Header */}
          <thead className="bg-gray-50 dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border">
            <tr role="row">
              {/* Select All Checkbox */}
              {selectable && (
                <th 
                  className="w-12 px-4 py-3"
                  role="columnheader"
                >
                  <Checkbox
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    indeterminate={selectedRows.size > 0 && selectedRows.size < paginatedData.length}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}

              {/* Column Headers */}
              {columns.map((column, index) => (
                <th
                  key={String(column.accessor) || index}
                  className={`px-4 py-3 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider ${getAlignmentClass(column.align)} ${column.hideOnMobile ? 'hidden md:table-cell' : ''}`}
                  style={{ width: column.width }}
                  role="columnheader"
                  aria-sort={
                    sortConfig.key === column.accessor
                      ? sortConfig.direction === 'asc'
                        ? 'ascending'
                        : sortConfig.direction === 'desc'
                        ? 'descending'
                        : 'none'
                      : 'none'
                  }
                >
                  {column.sortable ? (
                    <button
                      onClick={() => handleSort(String(column.accessor))}
                      className="flex items-center gap-1 hover:text-primary-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 rounded px-1 -mx-1 min-h-[32px]"
                      aria-label={`Sort by ${column.header}`}
                    >
                      {column.header}
                      {getSortIcon(String(column.accessor))}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
            {loading ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-12 text-center">
                  <div className="flex items-center justify-center gap-2 text-gray-500">
                    <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    <span>Loading...</span>
                  </div>
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (selectable ? 1 : 0)} className="px-4 py-12 text-center text-gray-500">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => {
                const actualIndex = (currentPage - 1) * pageSize + rowIndex;
                const isSelected = selectedRows.has(actualIndex);

                return (
                  <tr
                    key={actualIndex}
                    className={`transition-colors ${
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-dark-surface'
                    }`}
                    role="row"
                    aria-selected={isSelected}
                  >
                    {/* Row Selection Checkbox */}
                    {selectable && (
                      <td className="w-12 px-4 py-3" role="cell">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleRowSelect(actualIndex)}
                          aria-label={`Select row ${actualIndex + 1}`}
                        />
                      </td>
                    )}

                    {/* Row Cells */}
                    {columns.map((column, colIndex) => (
                      <td
                        key={String(column.accessor) || colIndex}
                        className={`px-4 py-3 text-sm text-gray-900 dark:text-dark-text ${getAlignmentClass(column.align)} ${column.hideOnMobile ? 'hidden md:table-cell' : ''}`}
                        role="cell"
                      >
                        {column.cell
                          ? column.cell(row, actualIndex)
                          : String(row[column.accessor] ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginated && filteredData.length > 0 && (
        <div className="mt-4 flex items-center justify-between px-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, filteredData.length)} of {filteredData.length} results
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-card border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-surface disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 min-h-[32px]"
              aria-label="Previous page"
            >
              Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber: number;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`min-w-[32px] min-h-[32px] px-2 py-1 text-sm font-medium rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 ${
                      currentPage === pageNumber
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-card border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-surface'
                    }`}
                    aria-label={`Page ${pageNumber}`}
                    aria-current={currentPage === pageNumber ? 'page' : undefined}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-card border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-surface disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 min-h-[32px]"
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

DataTable.displayName = 'DataTable';

export default DataTable;
